import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  FormControl,
  Button,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Box,
  Typography,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ReceiptLong as ReceiptLongIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../utils/swalConfig';
import { useNavigate } from 'react-router-dom';

const ExternalTransactionsPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  // API Functions
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://apidashboard.eurekagroup.id/external-transactions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      const transactions = result.data || [];
      setData(transactions);
      console.log('External transactions loaded:', result);
    } catch (error) {
      console.error('Error fetching external transactions:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal memuat data transaksi: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchTransactions();
  }, []);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type) => {
    switch (type?.toUpperCase()) {
      case 'USED':
        return 'error';
      case 'EARNED':
        return 'success';
      default:
        return 'info';
    }
  };

  const filteredData = data
    .filter(transaction => {
      const matchesSearch = transaction.transaction_code?.toLowerCase().includes(searchText.toLowerCase()) ||
                           transaction.no_reference?.toLowerCase().includes(searchText.toLowerCase()) ||
                           transaction.id_user?.toString().includes(searchText);
      const matchesStatus = statusFilter === '' || transaction.status === statusFilter;
      const matchesType = typeFilter === '' || transaction.type === typeFilter;
      
      return matchesSearch && matchesStatus && matchesType;
    })
    .sort((a, b) => {
      const dateA = new Date(a.date_created || 0);
      const dateB = new Date(b.date_created || 0);
      return dateB - dateA;
    });

  const uniqueStatuses = [...new Set(data.map(transaction => transaction.status))].filter(Boolean);
  const uniqueTypes = [...new Set(data.map(transaction => transaction.type))].filter(Boolean);

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <ReceiptLongIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  External Transactions
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola data transaksi eksternal dengan mudah dan efisien
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<RefreshIcon />} 
                onClick={fetchTransactions}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {loading ? <CircularProgress size={20} /> : 'Refresh'}
              </Button>
            </Box>
          }
          sx={{ pb: 2 }}
        />
        <Divider />
        <CardContent>
          {/* Filter Section */}
          <Box sx={{ 
            mb: 3, 
            p: 3, 
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200'
          }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Cari Transaksi"
                  variant="outlined"
                  value={searchText}
                  onChange={handleSearchChange}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    displayEmpty
                    startAdornment={
                      <InputAdornment position="start">
                        <FilterIcon color="action" />
                      </InputAdornment>
                    }
                    sx={{
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }}
                  >
                    <MenuItem value="">
                      <em>Semua Status</em>
                    </MenuItem>
                    {uniqueStatuses.map(status => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={typeFilter}
                    onChange={handleTypeFilterChange}
                    displayEmpty
                    sx={{
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }}
                  >
                    <MenuItem value="">
                      <em>Semua Tipe</em>
                    </MenuItem>
                    {uniqueTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {filteredData.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Total Transaksi
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Table Section */}
          <Box sx={{ 
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: 400,
                flexDirection: 'column',
                gap: 2
              }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                  Memuat data transaksi...
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          No
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Transaction Code
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Type
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Point
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Last Point
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Reference
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Date Created
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          textAlign: 'center'
                        }}>
                          Aksi
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((transaction, index) => (
                        <TableRow 
                          key={transaction.id_order}
                          sx={{ 
                            '&:hover': { 
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                              transform: 'scale(1.001)',
                              transition: 'all 0.2s ease'
                            },
                            '&:nth-of-type(even)': {
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover'
                            }
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>
                            {index + 1 + page * rowsPerPage}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {transaction.transaction_code || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={transaction.type || '-'}
                              color={getTypeColor(transaction.type)}
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: transaction.type === 'USED' ? 'error.main' : 'success.main' }}>
                              {transaction.type === 'USED' ? '-' : '+'}{formatCurrency(transaction.point)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatCurrency(transaction.last_point)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              {transaction.no_reference || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={transaction.status || '-'}
                              color={getStatusColor(transaction.status)}
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {formatDate(transaction.date_created)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <IconButton 
                                onClick={() => navigate(`/eureka-plus/external-transactions/view/${transaction.id_order}`)} 
                                sx={{ 
                                  color: 'primary.main',
                                  '&:hover': { 
                                    bgcolor: 'primary.light',
                                    color: 'white',
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                                size="small"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    borderTop: '1px solid',
                    borderColor: 'grey.200',
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                  }}
                />
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExternalTransactionsPage;




